import { useEffect, useState } from 'react'
import clsx from 'clsx'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { downloadFile } from '../../../../shared/helpers'

import Btn from '../../../../UI/Btn'

import styles from './style.module.scss'

/**
 * Die Teilnahmebestätigung zur Lernstrecke (Ergänzungsoption 5).
 *
 * Ein Blatt, das ausweist, welche Kapitel abgeschlossen sind und welche
 * Lernergebnisse aus den Skills4Retail-Modulen damit abgedeckt sind. Nicht zu
 * verwechseln mit dem **hochgeladenen** Kursnachweis im eigenen Profil — der
 * kommt von außerhalb und ist eine Voraussetzung der Aktivierung.
 *
 * **Deutsch, nicht über i18n** — wie der ganze Bereich Skills4Retail: Die zehn
 * Kapitel, die Quizfragen und die Statuswörter der Lernstrecke stehen als
 * deutscher Text im Code (`chapters.js`, `quizzes.js`, `STATUS_TEXT`). Ein
 * einzelner übersetzter Knopf mitten darin brächte nichts.
 *
 * **Ausgestellt wird erst bei vollständigem Abschluss.** Die erste Fassung gab
 * das Blatt ab dem ersten bestandenen Kapitel heraus und wies den jeweiligen
 * Stand aus. Das entwertet die Sache: Was wie eine Urkunde aussieht, sollte man
 * nicht nach zwanzig Minuten bekommen.
 *
 * **Seit 06.10.2026 erst nach bewertetem Konzept** (Anforderung #128): Zu den
 * Kapiteln der Person kommt, dass die Lehrkraft das Konzept der Junior Company
 * bewertet hat. Der Ausblick nennt deshalb beide Teile einzeln — wer alle
 * Kapitel hat, soll lesen, dass jetzt das Konzept fehlt, nicht „0 Kapitel".
 *
 * Solange etwas fehlt, steht hier trotzdem etwas — aber als **Ausblick**,
 * nicht als Knopf: wie viele noch offen sind und was danach kommt. Ein
 * ausgegrauter Knopf wäre eine Aufforderung ohne Ziel; gar nichts anzuzeigen
 * verschwiege, dass es die Bestätigung überhaupt gibt. Der Server antwortet auf
 * denselben Fall mit 404 — die Regel steht an beiden Enden.
 */
export default function CourseParticipation() {
	const [stand, setStand] = useState(null)
	const [laedt, setLaedt] = useState(false)

	useEffect(() => {
		// Ohne Toast: Steht die Bestätigung nicht zur Verfügung, ist das kein
		// Fehler, den jemand wegklicken müsste — der Knopf bleibt einfach weg.
		http.get(TApi.COURSE_PARTICIPATION, { disableToast: true })
			.then(({ data }) => setStand(data))
			.catch(() => {})
	}, [])

	const holen = () => {
		setLaedt(true)
		http.get(TApi.COURSE_PARTICIPATION_DOWNLOAD, { responseType: 'blob' })
			.then(({ data }) => downloadFile('Teilnahmebestaetigung-Skills4Retail.pdf', data))
			.finally(() => setLaedt(false))
	}

	if (!stand) return null

	const offen = (stand.chapterCount || 10) - (stand.passedChapters || 0)
	// `chaptersComplete`/`conceptScored` kennt erst der Server ab 06.10.2026;
	// ohne sie zählen wie vorher nur die Kapitel.
	const kapitelFertig = stand.chaptersComplete ?? offen <= 0
	const konzeptFehlt = stand.conceptScored === false

	// Noch nicht so weit: als Ausblick, ohne Knopf.
	if (!stand.available) {
		return (
			<div className={clsx(styles.participation, styles.participationOpen)}>
				<div>
					<b>Teilnahmebestätigung</b>
					<p>
						Wenn alle {stand.chapterCount} Kapitel abgeschlossen sind und die Lehrkraft euer Konzept
						bewertet hat, gibt es eine Teilnahmebestätigung als PDF — mit eurem Namen und den{' '}
						{stand.coveredCount} Lernergebnissen, die die Lernstrecke abdeckt.
					</p>
					<ul className={styles.participationMissing}>
						{!kapitelFertig && (
							<li>{offen === 1 ? 'Es fehlt noch ein Kapitel.' : `Es fehlen noch ${offen} Kapitel.`}</li>
						)}
						{kapitelFertig && <li>Alle Kapitel sind abgeschlossen.</li>}
						{konzeptFehlt && <li>Das Konzept ist noch nicht abgegeben und bewertet.</li>}
					</ul>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.participation}>
			<div>
				<b>Teilnahmebestätigung</b>
				<p>
					Alle {stand.chapterCount} Kapitel abgeschlossen und das Konzept bewertet — die Bestätigung weist
					alle {stand.achievedCount} behandelten Lernergebnisse aus.
				</p>
				<p className={styles.hinweis}>
					Sie wird beim ersten Holen abgelegt und steht danach unter „Dokumente“ — auch für die betreuende
					Lehrkraft.
				</p>
			</div>
			<Btn theme="secondary" onClick={holen} disabled={laedt}>
				{laedt ? 'Wird erstellt …' : 'Als PDF holen'}
			</Btn>
		</div>
	)
}
